import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { BankConnectionCard } from "@/components/tpaygate/BankConnectionCard";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGate } from "@/components/common/PermissionGate";
import { ConnectBankDialog } from "@/components/tpaygate/ConnectBankDialog";
import { DisconnectBankDialog } from "@/components/tpaygate/DisconnectBankDialog";
import {
  tPaygateConfigSchema,
  type TPaygateConfigForm,
} from "@/schemas/tpaygate.schema";
import { useQueryClient } from "@tanstack/react-query";
import {
  useTPaygateConfig,
  useConnectedBanks,
  useSaveConfig,
} from "@/hooks/useTPaygateQueries";
import type { ConnectedBankDto } from "@/types/tpaygate";
import {
  Link2,
  RefreshCw,
  Settings,
  Plus,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export function TPaygateSettingsPage() {
  // The gateway config and bank list are readable by SupportAdmin; every write here is SuperAdmin.
  const { hasRole } = usePermissions();
  const canManageGateway = hasRole("SuperAdmin");
  const [activeTab, setActiveTab] = useState("configuration");
  const [connectOpen, setConnectOpen] = useState(false);
  const [bankToDisconnect, setBankToDisconnect] =
    useState<ConnectedBankDto | null>(null);

  const queryClient = useQueryClient();
  const configQuery = useTPaygateConfig();
  const banksQuery = useConnectedBanks();
  const saveConfig = useSaveConfig();

  const form = useForm<TPaygateConfigForm>({
    resolver: zodResolver(tPaygateConfigSchema),
    defaultValues: {
      configId: "",
      clientId: "",
      tPayGateTenantId: "",
      source: "WEB",
      environment: "Staging",
      webhookUrl: "",
      clientSecret: "",
      isActive: true,
    },
  });

  useEffect(() => {
    if (configQuery.data) {
      form.reset({
        configId: configQuery.data.configId ?? "",
        clientId: configQuery.data.clientId,
        tPayGateTenantId: configQuery.data.tPayGateTenantId,
        source: configQuery.data.source,
        environment: configQuery.data.environment,
        webhookUrl: configQuery.data.webhookUrl ?? "",
        isActive: configQuery.data.isActive,

        // Blank on purpose: the API never returns the secret, and clearing the field after a save
        // keeps a freshly typed one from lingering in the DOM. Set explicitly rather than omitted,
        // so the input stays controlled instead of flipping to undefined.
        clientSecret: "",
      });
    }
  }, [configQuery.data, form]);

  const handleSaveConfig = async (values: TPaygateConfigForm) => {
    await saveConfig.mutateAsync({
      ...values,
      configId: values.configId || undefined,
      webhookUrl: values.webhookUrl || undefined,
      clientSecret: values.clientSecret || undefined,
    });
  };

  const handleConnected = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "tpaygate", "banks"] });
  };

  const connectedCount =
    banksQuery.data?.filter((b) => b.isConnected).length ?? 0;

  // Compared against the literal booleans on purpose. An API that predates hasClientSecret leaves
  // it undefined, and during a rollout this UI can talk to that older API — treating undefined as
  // "missing" would raise a false 403 alarm on a perfectly healthy integration.
  const secretMissing = configQuery.data?.hasClientSecret === false;
  const secretSaved = configQuery.data?.hasClientSecret === true;

  return (
    <div className="space-y-6">
      <PageHeader
        title="T-PayGate Integration"
        subtitle="Configure T-PayGate credentials and manage bank connections for QR payment processing"
        titleAdornment={
          <>
            {configQuery.data && !configQuery.data.isActive ? (
              <Badge variant="secondary">Disabled</Badge>
            ) : configQuery.data?.isConfigured ? (
              <Badge variant="default">Configured</Badge>
            ) : configQuery.data?.clientId && secretMissing ? (
              <Badge variant="destructive">Secret Missing</Badge>
            ) : (
              <Badge variant="secondary">Not Configured</Badge>
            )}
            {configQuery.data?.environment && (
              <Badge
                variant={
                  configQuery.data.environment === "Production"
                    ? "destructive"
                    : "outline"
                }
              >
                {configQuery.data.environment}
              </Badge>
            )}
          </>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="configuration" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="banks" className="gap-2">
            <Link2 className="h-4 w-4" />
            Bank Connections
            {connectedCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {connectedCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Configuration ── */}
        <TabsContent value="configuration" className="space-y-4 pt-4">
          {configQuery.data && !configQuery.data.isConfigured && (
            <Alert
              variant={
                configQuery.data.clientId && secretMissing
                  ? "destructive"
                  : "default"
              }
            >
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>
                {configQuery.data.clientId && secretMissing
                  ? "Client Secret Missing"
                  : "Setup Required"}
              </AlertTitle>
              <AlertDescription>
                {configQuery.data.clientId && secretMissing ? (
                  <>
                    Credentials are set but no client secret is stored, so every
                    T-PayGate request is rejected with 403 &quot;Invalid
                    signature&quot; and QR payments cannot be created. Re-enter
                    the secret below to restore them.
                  </>
                ) : (
                  <>
                    Enter your T-PayGate credentials (provided during
                    onboarding) to enable QR payment processing. Client ID,
                    Tenant ID and Client Secret are all required — a Config ID
                    on its own is not enough, because every request still has to
                    be signed with the secret.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>T-PayGate Credentials</CardTitle>
              <CardDescription>
                Credentials are provided by T-PayGate during onboarding. Keep
                them secret — never commit to Git or expose in logs.
              </CardDescription>
            </CardHeader>
            <CardContent className="relative">
              {/* Layer 2 of the submit-feedback standard: a button spinner alone is easy to miss
                  on a form this tall, and the fields must not be editable mid-request. */}
              {saveConfig.isPending && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md bg-background/70 backdrop-blur-[1px]">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Saving configuration…
                  </p>
                </div>
              )}

              {configQuery.isLoading ? (
                <LoadingSpinner />
              ) : configQuery.error ? (
                <ErrorFallback
                  error={configQuery.error as Error}
                  onRetry={() => configQuery.refetch()}
                />
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSaveConfig)}
                    aria-busy={saveConfig.isPending}
                    className={`space-y-4 transition-opacity ${
                      saveConfig.isPending
                        ? "pointer-events-none opacity-50"
                        : ""
                    }`}
                  >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="isActive"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2 flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5 pr-4">
                              <FormLabel className="text-base">
                                Enable T-PayGate
                              </FormLabel>
                              <FormDescription>
                                When off, T-PayGate is not offered as a payment
                                method and incoming payment webhooks are
                                rejected. Credentials are kept, so turning it
                                back on takes one switch.
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="environment"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2">
                            <FormLabel>Environment</FormLabel>
                            <FormControl>
                              <Select
                                key={field.value}
                                value={field.value}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Staging">
                                    Staging (UAT — t-paygate.tpos.dev)
                                  </SelectItem>
                                  <SelectItem value="Production">
                                    Production (t-paygate.tpos.app)
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormDescription>
                              Use Staging for testing. Switch to Production only
                              after passing all T-PayGate UAT test cases.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="configId"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2">
                            <FormLabel>Config ID</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="e.g. cfg_abc123"
                                autoComplete="off"
                              />
                            </FormControl>
                            <FormDescription>
                              If set, the payment flow uses this config ID in
                              the API header directly — no bank connection
                              required.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="clientId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Client ID{" "}
                              <span className="text-destructive-ink">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="demo_client"
                                autoComplete="off"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="tPayGateTenantId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              T-PayGate Tenant ID{" "}
                              <span className="text-destructive-ink">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="abc-123"
                                autoComplete="off"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="source"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Source{" "}
                              <span className="text-destructive-ink">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="WEB"
                                autoComplete="off"
                              />
                            </FormControl>
                            <FormDescription>
                              Channel identifier sent in OAuth requests (e.g.
                              WEB, MOBILE, POS)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="webhookUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Webhook URL</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="https://api.example.com/webhooks/tpaygate"
                                type="url"
                              />
                            </FormControl>
                            <FormDescription>
                              Public HTTPS endpoint that receives T-PayGate
                              payment callbacks
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="clientSecret"
                        render={({ field }) => (
                          <FormItem className="sm:col-span-2">
                            <FormLabel className="flex items-center gap-2">
                              Client Secret
                              {secretSaved && (
                                <Badge
                                  variant="outline"
                                  className="text-green-700 dark:text-green-400 border-green-500/30"
                                >
                                  Saved
                                </Badge>
                              )}
                              {secretMissing && (
                                <Badge variant="destructive">Missing</Badge>
                              )}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder={
                                  secretMissing
                                    ? "Enter client secret"
                                    : "Leave blank to keep the saved secret"
                                }
                                type="password"
                                autoComplete="new-password"
                              />
                            </FormControl>
                            <FormDescription>
                              {secretMissing ? (
                                <span className="text-destructive-ink">
                                  No secret is stored, so every T-PayGate call
                                  fails with 403 &quot;Invalid signature&quot;.
                                  Enter the secret to restore payments. If this
                                  tenant is a child tenant, use the parent
                                  tenant&apos;s secret.
                                </span>
                              ) : (
                                <>
                                  Secret key used to sign API requests
                                  (HMAC-SHA256). Provided by T-PayGate at
                                  onboarding. It is never displayed back —
                                  leaving this blank keeps the saved value.
                                </>
                              )}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex justify-end pt-2">
                      <PermissionGate roles="SuperAdmin">
                      <Button type="submit" disabled={saveConfig.isPending}>
                        {saveConfig.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                            Saving…
                          </>
                        ) : (
                          "Save Configuration"
                        )}
                      </Button>
                      </PermissionGate>
                    </div>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>

          {/* Flow reference */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Integration Flow</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                <li>
                  <strong className="text-foreground">Configure</strong> — Save
                  credentials above. The backend obtains an OAuth token (valid
                  60 min, auto-refreshed).
                </li>
                <li>
                  <strong className="text-foreground">Connect Bank</strong> — Go
                  to Bank Connections tab, add a bank account. T-PayGate returns
                  a VA number.
                </li>
                <li>
                  <strong className="text-foreground">Create Bills</strong> —
                  When a customer checks out, the backend creates a bill and
                  returns a VietQR code for the frontend to display.
                </li>
                <li>
                  <strong className="text-foreground">Receive Webhook</strong> —
                  T-PayGate POSTs{" "}
                  <code className="font-mono bg-muted px-1 rounded">
                    PAYMENT_RECEIVED
                  </code>{" "}
                  to your webhook URL when the customer pays.
                </li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Bank Connections ── */}
        <TabsContent value="banks" className="space-y-4 pt-4">
          {!configQuery.data?.isConfigured && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Configuration Required</AlertTitle>
              <AlertDescription>
                Save valid T-PayGate credentials — including the Client Secret —
                in the Configuration tab before connecting bank accounts. Bank
                connection calls are signed too, so a Config ID on its own does
                not make them work.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Connected Banks</h2>
              <p className="text-sm text-muted-foreground">
                Each connected bank generates a VA number used for QR payment
                processing
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => banksQuery.refetch()}
                disabled={banksQuery.isFetching}
              >
                <RefreshCw
                  className={`mr-1 h-3 w-3 ${banksQuery.isFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              <PermissionGate roles="SuperAdmin">
                <Button
                  size="sm"
                  onClick={() => setConnectOpen(true)}
                  disabled={!configQuery.data?.isConfigured}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Connect Bank
                </Button>
              </PermissionGate>
            </div>
          </div>

          {banksQuery.isLoading ? (
            <LoadingSpinner />
          ) : banksQuery.error ? (
            <ErrorFallback
              error={banksQuery.error as Error}
              onRetry={() => banksQuery.refetch()}
            />
          ) : banksQuery.data && banksQuery.data.length > 0 ? (
            <div className="space-y-3">
              {banksQuery.data.map((bank) => (
                <BankConnectionCard
                  key={bank.configBankId}
                  bank={bank}
                  // Disconnecting is SuperAdmin-only; the card hides its button without a handler.
                  onDisconnect={
                    canManageGateway ? (b) => setBankToDisconnect(b) : undefined
                  }
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-3">
                <Link2 className="h-10 w-10 text-muted-foreground" />
                <p className="text-muted-foreground">
                  No bank accounts connected yet
                </p>
                <p className="text-sm text-muted-foreground max-w-sm">
                  Connect a bank account to start accepting QR payments.
                  T-PayGate will generate a virtual account (VA) number for each
                  connection.
                </p>
                <PermissionGate roles="SuperAdmin">
                  <Button
                    onClick={() => setConnectOpen(true)}
                    disabled={!configQuery.data?.isConfigured}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Connect First Bank
                  </Button>
                </PermissionGate>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ConnectBankDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onConnected={handleConnected}
      />

      <DisconnectBankDialog
        bank={bankToDisconnect}
        open={!!bankToDisconnect}
        onClose={() => setBankToDisconnect(null)}
      />
    </div>
  );
}
